/**
 * NewslettersTab — card grid of the LO's newsletter series with live audience
 * counts, cadence chips, next-send countdown, last-issue engagement, and the
 * action menu (Edit / Send now / Send test / History / Archive).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  SimpleGrid,
  Text,
  Heading,
  HStack,
  VStack,
  Badge,
  Icon,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  IconButton,
  Button,
  Flex,
  Tag,
  Skeleton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useColorModeValue,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiMoreVertical,
  FiEdit2,
  FiSend,
  FiMail,
  FiClock,
  FiUsers,
  FiEye,
  FiArchive,
  FiList,
  FiPlus,
  FiCalendar,
  FiRefreshCw,
  FiAlertCircle,
} from 'react-icons/fi';
import moment from 'moment';
import { countAudience, updateNewsletter, archiveNewsletter } from '../../../../api/supabase/newsletters';
import { describeSchedule, computeNextRunAt } from '../../../../utils/newsletterSchedule';
import IssueHistoryDrawer from './IssueHistoryDrawer';

const STATUS_META = {
  draft: { label: 'Draft', colorScheme: 'gray' },
  active: { label: 'Active', colorScheme: 'green' },
  paused: { label: 'Paused', colorScheme: 'yellow' },
  archived: { label: 'Archived', colorScheme: 'red' },
};

const SKIP_REASON_LABELS = {
  unchanged_content: 'template unchanged since last issue',
  missing_template_content: 'template missing, empty, or deactivated',
};

function NewsletterCard({
  uid,
  newsletter,
  latestIssue,
  onEdit,
  onSendNow,
  onSendTest,
  onHistory,
  onArchive,
  onToggleActive,
  togglingId,
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const chipBg = useColorModeValue('gray.100', 'gray.700');
  const skipColor = useColorModeValue('orange.600', 'orange.300');

  const [audienceCount, setAudienceCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    countAudience(uid, newsletter.audienceConfig)
      .then((count) => { if (!cancelled) setAudienceCount(count); })
      .catch(() => { if (!cancelled) setAudienceCount(null); });
    return () => { cancelled = true; };
  }, [uid, newsletter.audienceConfig]);

  const statusMeta = STATUS_META[newsletter.status] || STATUS_META.draft;
  const canToggle = newsletter.status === 'active' || newsletter.status === 'paused';
  const openRate = latestIssue && latestIssue.sentCount > 0
    ? `${((latestIssue.uniqueOpens / latestIssue.sentCount) * 100).toFixed(0)}%`
    : null;

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      p={5}
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      display="flex"
      flexDirection="column"
    >
      <Flex justify="space-between" align="flex-start" mb={2}>
        <Box minW={0} pr={2} cursor="pointer" onClick={() => onEdit(newsletter)} flex={1}>
          <HStack spacing={2} mb={1}>
            <Badge colorScheme={statusMeta.colorScheme} variant="subtle">{statusMeta.label}</Badge>
            {newsletter.status === 'sending' && <Badge colorScheme="blue">Sending…</Badge>}
          </HStack>
          <Heading size="sm" noOfLines={1}>{newsletter.name}</Heading>
          {newsletter.description && (
            <Text fontSize="xs" color={mutedColor} noOfLines={1} mt={0.5}>
              {newsletter.description}
            </Text>
          )}
        </Box>
        <HStack spacing={1} flexShrink={0}>
          {canToggle && (
            <Tooltip label={newsletter.status === 'active' ? 'Pause schedule' : 'Resume schedule'} hasArrow fontSize="xs">
              <Box>
                <Switch
                  size="sm"
                  colorScheme="green"
                  isChecked={newsletter.status === 'active'}
                  isDisabled={togglingId === newsletter.id}
                  onChange={() => onToggleActive(newsletter)}
                />
              </Box>
            </Tooltip>
          )}
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              icon={<FiMoreVertical />}
              variant="ghost"
              size="sm"
              aria-label="Newsletter actions"
            />
            <MenuList fontSize="sm">
              <MenuItem icon={<FiEdit2 />} onClick={() => onEdit(newsletter)}>Edit</MenuItem>
              <MenuItem icon={<FiSend />} onClick={() => onSendNow(newsletter, audienceCount)}>Send now</MenuItem>
              <MenuItem icon={<FiMail />} onClick={() => onSendTest(newsletter)}>Send me a test</MenuItem>
              <MenuItem icon={<FiList />} onClick={() => onHistory(newsletter)}>Issue history</MenuItem>
              <MenuDivider />
              <MenuItem icon={<FiArchive />} color="red.400" onClick={() => onArchive(newsletter)}>
                Archive
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <HStack spacing={2} mt={2} flexWrap="wrap">
        <Tag size="sm" bg={chipBg} borderRadius="full">
          <Icon as={FiCalendar} boxSize={3} mr={1} />
          {describeSchedule(newsletter.scheduleConfig)}
        </Tag>
        <Tag size="sm" bg={chipBg} borderRadius="full">
          <Icon as={FiUsers} boxSize={3} mr={1} />
          {audienceCount === null ? '…' : `${audienceCount} recipient${audienceCount === 1 ? '' : 's'}`}
        </Tag>
        {newsletter.contentSource === 'template' && (
          <Tooltip
            label="Content stays synced to a company template — every send uses its latest version"
            hasArrow
            fontSize="xs"
          >
            <Tag size="sm" colorScheme="blue" variant="subtle" borderRadius="full">
              <Icon as={FiRefreshCw} boxSize={3} mr={1} />
              Synced template
            </Tag>
          </Tooltip>
        )}
      </HStack>

      <VStack align="stretch" spacing={1.5} mt={4} fontSize="xs" color={mutedColor}>
        <HStack justify="space-between">
          <HStack spacing={1}><Icon as={FiClock} boxSize={3} /><Text>Next send</Text></HStack>
          <Text fontWeight="medium">
            {newsletter.status === 'active' && newsletter.nextRunAt
              ? moment(newsletter.nextRunAt).format('MMM D, h:mm A') + ` (${moment(newsletter.nextRunAt).fromNow()})`
              : '—'}
          </Text>
        </HStack>
        <HStack justify="space-between">
          <HStack spacing={1}><Icon as={FiSend} boxSize={3} /><Text>Last sent</Text></HStack>
          <Text fontWeight="medium">
            {newsletter.lastSentAt ? moment(newsletter.lastSentAt).fromNow() : 'Never'}
          </Text>
        </HStack>
        {newsletter.lastSkippedAt && (
          <Tooltip
            label="The scheduled send was skipped so your realtors don't get duplicate content. It sends automatically next cycle once the template is updated."
            hasArrow
            fontSize="xs"
          >
            <HStack justify="space-between" color={skipColor}>
              <HStack spacing={1}><Icon as={FiAlertCircle} boxSize={3} /><Text>Last check</Text></HStack>
              <Text fontWeight="medium" noOfLines={1}>
                Skipped {moment(newsletter.lastSkippedAt).fromNow()} — {SKIP_REASON_LABELS[newsletter.lastSkipReason] || 'skipped'}
              </Text>
            </HStack>
          </Tooltip>
        )}
        <HStack justify="space-between">
          <HStack spacing={1}><Icon as={FiEye} boxSize={3} /><Text>Last issue opens</Text></HStack>
          <Text fontWeight="medium">
            {openRate
              ? `${openRate} (${latestIssue.uniqueOpens}/${latestIssue.sentCount})`
              : '—'}
          </Text>
        </HStack>
      </VStack>
    </Box>
  );
}

export default function NewslettersTab({ uid, newslettersHook, onCreate }) {
  const history = useHistory();
  const toast = useToast();
  const {
    newsletters,
    latestIssueByNewsletter,
    loading,
    refresh,
    sendNewsletterNow,
    sendTestNewsletter,
  } = newslettersHook;

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [togglingId, setTogglingId] = useState(null);
  const [confirmSend, setConfirmSend] = useState(null); // { newsletter, audienceCount }
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [sending, setSending] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const cancelRef = useRef();

  const handleEdit = useCallback((newsletter) => {
    history.push(`/crm/my-marketing/newsletters/${newsletter.id}`);
  }, [history]);

  const handleToggleActive = async (newsletter) => {
    setTogglingId(newsletter.id);
    try {
      if (newsletter.status === 'active') {
        await updateNewsletter(newsletter.id, { status: 'paused', nextRunAt: null });
        toast({ title: 'Newsletter paused', status: 'info', duration: 2500, isClosable: true });
      } else {
        const nextRun = computeNextRunAt(newsletter.scheduleConfig);
        if (!nextRun) {
          toast({
            title: 'No upcoming send on this schedule',
            description: 'Open the newsletter and set its schedule before activating.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        if (!newsletter.subject || !newsletter.bodyHtml) {
          toast({
            title: 'Add content first',
            description: 'A subject and body are required before activating.',
            status: 'warning',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        await updateNewsletter(newsletter.id, { status: 'active', nextRunAt: nextRun.toISOString() });
        toast({
          title: 'Newsletter activated',
          description: `Next send ${moment(nextRun).format('MMM D [at] h:mm A')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      refresh();
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setTogglingId(null);
    }
  };

  const handleConfirmSendNow = async () => {
    if (!confirmSend) return;
    setSending(true);
    try {
      const result = await sendNewsletterNow(confirmSend.newsletter.id);
      const mins = Math.max(1, Math.ceil((result.queuedCount || 0) / 25));
      toast({
        title: 'Newsletter is on its way',
        description: `${result.queuedCount} emails queued — delivers over ~${mins} minute${mins === 1 ? '' : 's'}.`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
      setConfirmSend(null);
      refresh();
    } catch (err) {
      toast({ title: 'Send failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async (newsletter) => {
    toast({ title: 'Sending test email…', status: 'info', duration: 2000 });
    try {
      const result = await sendTestNewsletter({ newsletterId: newsletter.id });
      toast({
        title: 'Test sent',
        description: `Check ${result.sentTo}${result.sampleContactName ? ` — merged as if sent to ${result.sampleContactName}` : ''}`,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (err) {
      toast({ title: 'Test send failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleConfirmArchive = async () => {
    if (!confirmArchive) return;
    try {
      await archiveNewsletter(confirmArchive.id);
      toast({ title: 'Newsletter archived', status: 'info', duration: 2500, isClosable: true });
      setConfirmArchive(null);
      refresh();
    } catch (err) {
      toast({ title: 'Archive failed', description: err.message, status: 'error', duration: 4000 });
    }
  };

  if (!loading && newsletters.length === 0) {
    return (
      <Flex
        data-tour="mm-newsletters-list"
        direction="column"
        align="center"
        justify="center"
        py={16}
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        borderStyle="dashed"
        gap={3}
      >
        <Icon as={FiSend} boxSize={10} color="blue.300" />
        <Heading size="sm">No newsletters yet</Heading>
        <Text fontSize="sm" color={mutedColor} maxW="420px" textAlign="center">
          Keep your realtors engaged with a recurring newsletter — market updates, rate news,
          co-marketing ideas. Set it up once and it sends itself.
        </Text>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={onCreate} mt={2}>
          Create your first newsletter
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4} data-tour="mm-newsletters-list">
        {loading && newsletters.length === 0
          ? [0, 1, 2].map((i) => <Skeleton key={i} h="230px" borderRadius="xl" />)
          : newsletters.map((newsletter) => (
            <NewsletterCard
              key={newsletter.id}
              uid={uid}
              newsletter={newsletter}
              latestIssue={latestIssueByNewsletter[newsletter.id]}
              onEdit={handleEdit}
              onSendNow={(nl, audienceCount) => setConfirmSend({ newsletter: nl, audienceCount })}
              onSendTest={handleSendTest}
              onHistory={setHistoryTarget}
              onArchive={setConfirmArchive}
              onToggleActive={handleToggleActive}
              togglingId={togglingId}
            />
          ))}
      </SimpleGrid>

      {/* Send-now confirmation */}
      <AlertDialog
        isOpen={!!confirmSend}
        leastDestructiveRef={cancelRef}
        onClose={() => !sending && setConfirmSend(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg">Send "{confirmSend?.newsletter?.name}" now?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              This sends immediately to{' '}
              <b>{confirmSend?.audienceCount ?? 'your'}</b> recipient{confirmSend?.audienceCount === 1 ? '' : 's'} from
              your Gmail. Emails drain at ~25/minute
              {confirmSend?.audienceCount
                ? ` (about ${Math.max(1, Math.ceil(confirmSend.audienceCount / 25))} min)`
                : ''}. Unsubscribed and Do-Not-Contact realtors are automatically excluded.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setConfirmSend(null)} isDisabled={sending}>
                Cancel
              </Button>
              <Button colorScheme="blue" leftIcon={<FiSend />} onClick={handleConfirmSendNow} ml={3} isLoading={sending}>
                Send now
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Archive confirmation */}
      <AlertDialog
        isOpen={!!confirmArchive}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmArchive(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg">Archive "{confirmArchive?.name}"?</AlertDialogHeader>
            <AlertDialogBody fontSize="sm">
              The schedule stops and the newsletter is hidden from this list. Past issues and
              stats are kept.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={() => setConfirmArchive(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmArchive} ml={3}>
                Archive
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <IssueHistoryDrawer
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        newsletter={historyTarget}
      />
    </>
  );
}
